// src/components/catalog/BundleControls.js
//
// Bir bundle'ın yayın kontrolleri: aktif/pasif anahtarı + Play'e yayınla/tazele.
// Katalog sayfasının tekelindeydi; tarife sayfaları kendi kartlarına bunu koyar,
// mantık her sayfada ayrı ayrı kopyalanmasın diye burada.

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import StatusToggle from '../common/StatusToggle';
import { hasPricedPlan, publishBundleToPlay, readApiError, updateBundle } from '../../services/catalogApi';
import styles from './BundleControls.module.css';

/**
 * `onUpdated(bundleId, fields)` — sayfa kendi listesindeki bundle'a `fields`'ı işler.
 */
const BundleControls = ({ bundle, onUpdated }) => {
    const [isToggling, setIsToggling] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    const bundleId = String(bundle.id);
    const priced = hasPricedPlan(bundle);
    const published = !!bundle.playPublishedAt;

    const handleToggleActive = async () => {
        const next = !bundle.isActive;
        if (next && !priced) {
            toast.error('Bu paketin hiçbir planında fiyat yok; yayına alınamaz.');
            return;
        }

        setIsToggling(true);
        try {
            await updateBundle(bundle.id, { isActive: next });
            onUpdated(bundle.id, { isActive: next });
            toast.success(next ? `${bundle.title} yayına alındı.` : `${bundle.title} yayından çıkarıldı.`);
        } catch (err) {
            console.error(`Bundle ${bundleId} durumu değiştirilemedi:`, err);
            toast.error(readApiError(err, 'Durum değiştirilemedi.').message);
        } finally {
            setIsToggling(false);
        }
    };

    const handlePublishToPlay = async () => {
        if (!priced) {
            toast.error('Önce en az bir plana taban fiyat girin; fiyatsız plan yayına dahil edilmez.');
            return;
        }
        // Yeniden yayınlamak güvenli (fiyatlar tazelenir) ama Google'a gerçek çağrı;
        // yanlışlıkla tıklamayı onayla.
        const msg = published
            ? `${bundle.title} zaten Play'de yayınlı. Yeniden yayınlamak option fiyatlarını mevcut taban fiyatlardan tazeler. Devam edilsin mi?`
            : `${bundle.title} Google Play'e TEK ürün olarak yayınlanacak (bundle_${bundleId}). Fiyatı girilmiş her plan bir satın alma seçeneği olur. Devam edilsin mi?`;
        if (!window.confirm(msg)) return;

        setIsPublishing(true);
        try {
            const result = await publishBundleToPlay(bundle.id);
            onUpdated(bundle.id, { playPublishedAt: new Date().toISOString() });
            toast.success(
                `Play'e yayınlandı: ${result.productId} — ${result.pricedPlanCount}/${result.planCount} plan fiyatlandı.`
            );
            if (result.skippedOverPriceCap?.length) {
                // Google, taban TRY'yi ~170 para birimine çevirir ve her ülkenin fiyat
                // tavanı vardır; taşan plan yayından otomatik çıkarılır.
                toast.warn(
                    `${result.skippedOverPriceCap.length} plan bölgesel fiyat tavanına takıldı ve yayına DAHİL EDİLMEDİ: `
                    + `${result.skippedOverPriceCap.join(', ')}. Fiyatı düşürüp yeniden yayınlayın.`,
                    { autoClose: 12000 }
                );
            }
        } catch (err) {
            console.error(`Bundle ${bundleId} Play'e yayınlanamadı:`, err);
            toast.error(readApiError(err, "Play'e yayınlanamadı.").message);
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <div className={styles.controls}>
            <StatusToggle
                label={`${bundle.title} yayın durumu`}
                checked={bundle.isActive}
                saving={isToggling}
                disabled={!priced && !bundle.isActive}
                onChange={handleToggleActive}
                onText="Yayında"
                offText="Yayına kapalı"
            />
            <button
                type="button"
                className={`${styles.playButton} ghost`}
                disabled={isPublishing || (!priced && !published)}
                title={published
                    ? `Play'de yayınlı (bundle_${bundleId}) — yeniden yayınlamak fiyatları tazeler`
                    : 'Google Play\'e tek ürün olarak yayınlar; planlar satın alma seçeneği olur'}
                onClick={handlePublishToPlay}
            >
                {isPublishing ? 'Yayınlanıyor…' : published ? 'Play ✓ (tazele)' : "Play'e yayınla"}
            </button>
        </div>
    );
};

export default BundleControls;
